import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
    createPayment,
    pollPaymentStatus,
    PAYMENT_PACKAGES,
    PAYMENT_CHANNELS,
    type PaymentPackage,
    type PaymentChannel,
    type CreatePaymentResponse,
    type PaymentStatusResponse
} from '../api/alipayClient';
import { showToast } from '../../../shared/notifications/toastStore';
import { VoucherRedeemForm } from '../../auth/components/VoucherRedeemForm';
import { QrCodeCanvas } from './QrCodeCanvas';

interface PricingModalProps {
    onClose: () => void;
    onPaymentSuccess?: (points: number, balance?: number) => void;
}

type PaymentStep = 'select' | 'paying' | 'success' | 'failed';

export function PricingModal({ onClose, onPaymentSuccess }: PricingModalProps) {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();

    const [step, setStep] = useState<PaymentStep>('select');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showVoucher, setShowVoucher] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState<PaymentChannel>(PAYMENT_CHANNELS[0]);
    const [currentOrder, setCurrentOrder] = useState<CreatePaymentResponse | null>(null);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const cancelPollRef = useRef<(() => void) | null>(null);

    // 清理轮询
    useEffect(() => {
        return () => {
            if (cancelPollRef.current) {
                cancelPollRef.current();
            }
        };
    }, []);

    // 跳转支付宝后用浏览器返回键回来时页面从 bfcache 恢复，按钮会卡在「处理中」
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setIsSubmitting(false);
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        contentRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === overlayRef.current) {
            onClose();
        }
    };

    const handlePaymentSuccess = useCallback((response: PaymentStatusResponse) => {
        setStep('success');
        showToast({ message: t('payment.success'), tone: 'success' });
        if (onPaymentSuccess) {
            onPaymentSuccess(response.points, response.balance);
        }
        setTimeout(onClose, 2000);
    }, [t, onClose, onPaymentSuccess]);

    const handlePaymentFailed = useCallback(() => {
        setStep('failed');
        showToast({ message: t('payment.failed'), tone: 'danger' });
    }, [t]);

    const handlePurchase = async (pkg: PaymentPackage) => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            console.info('[payment] channel:', selectedChannel.code, 'package:', pkg.id);
            const response = await createPayment(pkg.id, selectedChannel.code);
            console.info('[payment] response:', JSON.stringify(response));
            setCurrentOrder(response);

            if (response.paymentType === 'REDIRECT') {
                if (!response.form) {
                    throw new Error('Payment form is empty for REDIRECT type');
                }
                // 跳转支付 (支付宝)
                showToast({ message: t('payment.redirecting'), tone: 'info' });

                const div = document.createElement('div');
                div.innerHTML = response.form;
                document.body.appendChild(div);

                const form = div.querySelector('form');
                if (form) {
                    // 安全校验：确保表单 action 指向支付宝网关
                    const action = form.getAttribute('action') || '';
                    const allowedHosts = ['openapi.alipay.com', 'openapi-sandbox.dl.alipaydev.com'];
                    try {
                        const url = new URL(action);
                        if (!allowedHosts.some(h => url.hostname.endsWith(h))) {
                            document.body.removeChild(div);
                            throw new Error('Untrusted payment gateway');
                        }
                    } catch (urlError) {
                        if (urlError instanceof TypeError) {
                            document.body.removeChild(div);
                            throw new Error('Invalid payment form URL');
                        }
                        throw urlError;
                    }
                    form.submit();
                } else {
                    document.body.removeChild(div);
                    throw new Error('Invalid payment form received');
                }
            } else if (response.paymentType === 'QR_CODE') {
                if (!response.qrCodeUrl) {
                    throw new Error('QR code URL is empty');
                }
                // 扫码支付 (微信)
                setStep('paying');
                setQrCodeUrl(response.qrCodeUrl);

                // 开始轮询支付状态
                cancelPollRef.current = pollPaymentStatus(
                    response.orderNo,
                    handlePaymentSuccess,
                    handlePaymentFailed
                );
            } else {
                console.warn('Unexpected payment response:', JSON.stringify(response));
                throw new Error('Unknown payment type: ' + response?.paymentType);
            }
        } catch (error) {
            console.error('Payment creation failed', error);
            showToast({ message: t('memberPage.errors.generic'), tone: 'danger' });
            setIsSubmitting(false);
        }
    };

    const handleRetry = () => {
        setStep('select');
        setIsSubmitting(false);
        setCurrentOrder(null);
        setQrCodeUrl(null);
        if (cancelPollRef.current) {
            cancelPollRef.current();
            cancelPollRef.current = null;
        }
    };

    // 支付中 - 显示二维码
    if (step === 'paying' && qrCodeUrl) {
        return createPortal(
            <div
                ref={overlayRef}
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6"
                onMouseDown={handleBackdropClick}
            >
                <div
                    ref={contentRef}
                    className="relative w-full max-w-md overflow-hidden rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/95 shadow-ai-card p-8 text-center"
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                >
                    <h3 className="text-xl font-semibold text-ai-text-primary mb-4">
                        {t('payment.scanToPay')}
                    </h3>
                    <p className="text-ai-text-secondary mb-6">
                        {t('payment.scanWithWechat')}
                    </p>

                    <div className="bg-white p-4 rounded-2xl inline-block mb-6">
                        <QrCodeCanvas value={qrCodeUrl} size={192} />
                    </div>

                    <div className="text-sm text-ai-text-secondary mb-4">
                        {t('payment.waitingForPayment')}
                        <span className="inline-block animate-pulse ml-2">●</span>
                    </div>

                    <button
                        onClick={handleRetry}
                        className="text-sm text-ai-text-secondary underline hover:text-ai-primary"
                    >
                        {t('payment.changeMethod')}
                    </button>
                </div>
            </div>,
            document.body
        );
    }

    // 支付成功
    if (step === 'success') {
        return createPortal(
            <div
                ref={overlayRef}
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6"
            >
                <div className="w-full max-w-md rounded-3xl border border-ai-primary/40 bg-ai-surface-elevated/95 p-8 text-center">
                    <div className="text-6xl mb-4">✅</div>
                    <h3 className="text-xl font-semibold text-ai-text-primary mb-2">
                        {t('payment.success')}
                    </h3>
                    <p className="text-ai-text-secondary">
                        {t('payment.successDesc', { points: currentOrder?.points || 0 })}
                    </p>
                </div>
            </div>,
            document.body
        );
    }

    // 支付失败
    if (step === 'failed') {
        return createPortal(
            <div
                ref={overlayRef}
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6"
            >
                <div className="w-full max-w-md rounded-3xl border border-red-500/40 bg-ai-surface-elevated/95 p-8 text-center">
                    <div className="text-6xl mb-4">❌</div>
                    <h3 className="text-xl font-semibold text-ai-text-primary mb-2">
                        {t('payment.failed')}
                    </h3>
                    <p className="text-ai-text-secondary mb-6">
                        {t('payment.failedDesc')}
                    </p>
                    <button
                        onClick={handleRetry}
                        className="px-6 py-2 bg-ai-primary text-black rounded-xl font-medium hover:bg-ai-primary/90"
                    >
                        {t('payment.retry')}
                    </button>
                </div>
            </div>,
            document.body
        );
    }

    // 兑换码视图
    if (showVoucher) {
        return createPortal(
            <div
                ref={overlayRef}
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6 sm:px-6 sm:py-10"
                onMouseDown={handleBackdropClick}
            >
                <div
                    ref={contentRef}
                    className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/95 shadow-ai-card focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        type="button"
                        className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-ai-surface-glow/30 text-sm text-ai-text-secondary transition-colors hover:border-ai-primary/60 hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                        onClick={onClose}
                    >
                        X
                    </button>

                    <div className="p-6 sm:p-8">
                        <div className="mb-6">
                            <button
                                onClick={() => setShowVoucher(false)}
                                className="text-sm text-ai-text-secondary hover:text-ai-primary flex items-center gap-1 transition-colors"
                            >
                                ← {t('payment.title')}
                            </button>
                        </div>
                        <VoucherRedeemForm
                            variant="authenticated"
                            onSuccess={() => {
                                showToast({ message: t('voucherActivation.toast.topUpSuccess'), tone: 'success' });
                                setTimeout(onClose, 1500);
                            }}
                        />
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    // 主选择视图
    return createPortal(
        <div
            ref={overlayRef}
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6 sm:px-6 sm:py-10"
            onMouseDown={handleBackdropClick}
        >
            <div
                ref={contentRef}
                className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/95 shadow-ai-card focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                role="dialog"
                aria-modal="true"
                aria-label={t('payment.title')}
                tabIndex={-1}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-ai-surface-glow/30 text-sm text-ai-text-secondary transition-colors hover:border-ai-primary/60 hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:right-5 sm:top-5 sm:h-10 sm:w-10"
                    aria-label={t('app.actions.close')}
                    onClick={onClose}
                >
                    X
                </button>

                <div className="max-h-[calc(100vh-4rem)] overflow-y-auto p-6 sm:p-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ai-text-primary sm:text-4xl">
                            {t('payment.title')}
                        </h2>
                        <p className="mt-2 text-ai-text-secondary sm:text-lg">
                            {t('payment.subtitle')}
                        </p>
                    </div>

                    {/* 支付方式选择 */}
                    <div className="flex justify-center gap-4 mb-8">
                        {PAYMENT_CHANNELS.map((channel) => (
                            <button
                                key={channel.code}
                                onClick={() => !channel.disabled && setSelectedChannel(channel)}
                                disabled={channel.disabled}
                                className={clsx(
                                    'flex items-center gap-2 px-6 py-3 rounded-xl border transition-all',
                                    channel.disabled
                                        ? 'border-transparent bg-gray-100/50 text-gray-400 cursor-not-allowed grayscale opacity-60'
                                        : selectedChannel.code === channel.code
                                            ? 'border-ai-primary bg-ai-primary/10 text-ai-text-primary'
                                            : 'border-ai-surface-glow/30 text-ai-text-secondary hover:border-ai-primary/50'
                                )}
                                title={channel.disabled ? t('payment.comingSoon', '敬请期待') : ''}
                            >
                                <span className="text-xl">{channel.icon}</span>
                                <span className="font-medium">{channel.name}</span>
                            </button>
                        ))}
                    </div>

                    <div className="grid gap-6 md:grid-cols-3 sm:gap-8 max-w-5xl mx-auto">
                        {PAYMENT_PACKAGES.map((pkg) => {
                            const features = t(pkg.featuresKey, { returnObjects: true }) as string[];
                            return (
                                <div
                                    key={pkg.id}
                                    className={clsx(
                                        'group relative flex flex-col rounded-3xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-ai-card',
                                        pkg.recommended
                                            ? 'border-ai-primary/50 bg-gradient-to-b from-ai-primary/10 to-ai-surface-elevated shadow-lg ring-1 ring-ai-primary/30'
                                            : 'border-ai-surface-glow/30 bg-ai-surface-elevated/50 hover:border-ai-primary/30'
                                    )}
                                >
                                    {pkg.recommended && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-ai-primary to-ai-secondary px-4 py-1 text-xs font-bold uppercase tracking-widest text-black shadow-lg">
                                            {t('payment.bestValue')}
                                        </div>
                                    )}

                                    <div className="mb-4">
                                        <h3 className="text-sm font-semibold uppercase tracking-widest text-ai-text-secondary">
                                            {t(pkg.labelKey)}
                                        </h3>
                                        <div className="mt-2 flex items-baseline gap-1">
                                            <span className="text-base font-medium text-ai-text-secondary">¥</span>
                                            <span className="font-display text-4xl font-bold text-ai-text-primary">
                                                {pkg.price}
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm text-ai-text-secondary">
                                            {t(pkg.descriptionKey)}
                                        </p>
                                    </div>

                                    <ul className="mb-8 flex-1 space-y-3">
                                        {Array.isArray(features) && features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-sm text-ai-text-secondary/90">
                                                <svg className="h-5 w-5 shrink-0 text-ai-primary" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => handlePurchase(pkg)}
                                        disabled={isSubmitting}
                                        className={clsx(
                                            'w-full rounded-xl px-4 py-3 text-sm font-semibold uppercase tracking-widest transition-all focus:outline-none focus-visible:ring focus-visible:ring-ai-focus',
                                            pkg.recommended
                                                ? 'bg-ai-primary text-black hover:bg-ai-primary/90 hover:shadow-lg'
                                                : 'bg-ai-surface-glow/20 text-ai-text-primary hover:bg-ai-surface-glow/40 hover:text-white'
                                        )}
                                    >
                                        {isSubmitting ? t('payment.processing') : t('payment.pay')}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-10 text-center">
                        <button
                            onClick={() => setShowVoucher(true)}
                            className="text-sm text-ai-text-secondary underline decoration-1 underline-offset-4 hover:text-ai-primary transition-colors"
                        >
                            {t('account.actions.activateVoucher')}
                        </button>
                        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-ai-text-secondary/50">
                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm0-4H9V7h2v5z" />
                            </svg>
                            {t('payment.securePayment')}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
